import * as React from "react";
import { Card, CardBody, PageContainer } from "../components/Card";
import { PageHeader, SectionHeader } from "../components/PageHeader";
import { Button, IconButton } from "../components/Button";
import { Modal, ModalBody } from "../components/Modal";
import { Field, Select } from "../components/Form";
import { Table, Thead, Tbody, Tr, Th, Td, TdActions } from "../components/Table";
import { Badge } from "../components/Badge";
import { Banner, EmptyState, ListStates } from "../components/States";
import { Toolbar } from "../components/Toolbar";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { CalculationFinding } from "../api/errors";
import { ModulePicker } from "../activities/ModulePicker";
import { FindingList, FindingSummary } from "./FindingList";
import * as Store from "./ProceduresStore";

/**
 * Binding a published procedure version to a qualification module.
 *
 * An assignment is what turns a set of rules into concrete `Calculation` edges. The lifecycle is:
 * create it, check it resolves against the module, activate it. Regenerate whenever the assessment
 * structure underneath changes, because the edges were expanded from the structure as it was then.
 *
 * "Check against module" (resolve-exact-selectors) is the cheapest way to find out that a rule
 * matches nothing - usually because a group type is missing from the tree.
 */

export const AssignmentsIndex: React.FC = () => {
    const toast = useToast();
    const canAssign = usePermission(Permissions.PROCEDURES_ASSIGN);

    const [moduleID, setModuleID] = React.useState("");
    const [creating, setCreating] = React.useState(false);
    const [findings, setFindings] = React.useState<CalculationFinding[]>([]);
    const [resolution, setResolution] = React.useState<Store.ExactSelectorResolutionModel | null>(null);

    const assignments = useAsync(() => Store.listAssignments(moduleID ? { qualificationModuleID: moduleID } : {}), [moduleID], {
        initial: [],
    });

    const act = async (label: string, run: () => Promise<Store.AssignmentGenerationModel>) => {
        const result = await run();
        setFindings(result.findings ?? []);
        toast.success(`${label}. ${result.generatedEdges ?? 0} calculation edge(s) generated.`);
        assignments.refresh();
    };

    return (
        <PageContainer>
            <PageHeader
                title="Procedure assignments"
                icon={["fad", "link"]}
                description="Which procedure version runs against which qualification module."
                actions={
                    canAssign && (
                        <Button icon={["fad", "plus"]} onClick={() => setCreating(true)}>
                            New assignment
                        </Button>
                    )
                }
            />

            <Toolbar>
                <ModulePicker value={moduleID} onChange={setModuleID} label="Filter by qualification module" />
                {moduleID && (
                    <Button variant="ghost" size="sm" onClick={() => setModuleID("")}>
                        Clear filter
                    </Button>
                )}
            </Toolbar>

            <Card>
                <ListStates
                    isLoading={assignments.isLoading}
                    error={assignments.error}
                    onRetry={assignments.refresh}
                    isEmpty={(assignments.data ?? []).length === 0}
                    empty={{
                        icon: ["fad", "link"],
                        title: "No assignments",
                        hint: moduleID
                            ? "This module has no procedure bound to it yet."
                            : "Bind a published procedure version to a qualification module to start calculating.",
                    }}
                >
                    <Table>
                        <Thead>
                            <Tr>
                                <Th>Procedure</Th>
                                <Th>Version</Th>
                                <Th>Qualification module</Th>
                                <Th align="right">Edges</Th>
                                <Th>State</Th>
                                <Th align="right">Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {(assignments.data ?? []).map((a) => (
                                <AssignmentRow
                                    key={a.id}
                                    assignment={a}
                                    canAssign={canAssign}
                                    onAction={act}
                                    onResolved={(r) => {
                                        setResolution(r);
                                        setFindings(r.findings ?? []);
                                    }}
                                />
                            ))}
                        </Tbody>
                    </Table>
                </ListStates>
            </Card>

            {resolution && (
                <Card className="mt-6">
                    <CardBody>
                        <SectionHeader
                            title="Selector resolution"
                            description="What the version's selectors actually match in that module, right now."
                            actions={
                                <Button size="sm" variant="ghost" onClick={() => setResolution(null)}>
                                    Dismiss
                                </Button>
                            }
                        />
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-text-weak">
                                    Targets ({resolution.targets?.length ?? 0})
                                </h4>
                                {(resolution.targets ?? []).length === 0 ? (
                                    <p className="text-sm text-danger-text">
                                        Nothing matched. This assignment would produce no marks at all.
                                    </p>
                                ) : (
                                    <ul className="space-y-1 text-sm">
                                        {(resolution.targets ?? []).map((t, i) => (
                                            <li key={i} className="flex items-center gap-2">
                                                <Badge tone="primary">{t.entityType}</Badge>
                                                <span className="font-mono text-xs text-text-weak">{t.entityID}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div>
                                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-text-weak">
                                    Inputs ({resolution.inputs?.length ?? 0})
                                </h4>
                                {(resolution.inputs ?? []).length === 0 ? (
                                    <p className="text-sm text-danger-text">
                                        No inputs matched. Check the group types in the module&rsquo;s assessment tree.
                                    </p>
                                ) : (
                                    <ul className="space-y-1 text-sm">
                                        {(resolution.inputs ?? []).map((input: any, i) => (
                                            <li key={i} className="flex items-center gap-2">
                                                <Badge tone="neutral">{input.entityType}</Badge>
                                                <span className="font-mono text-xs text-text-weak">{input.entityID}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </CardBody>
                </Card>
            )}

            {findings.length > 0 && (
                <Card className="mt-6">
                    <CardBody>
                        <SectionHeader title="Findings" actions={<FindingSummary findings={findings} />} />
                        <FindingList findings={findings} />
                    </CardBody>
                </Card>
            )}

            <CreateAssignmentModal
                isOpen={creating}
                onClose={() => setCreating(false)}
                onCreated={(result) => {
                    setCreating(false);
                    setFindings(result.findings ?? []);
                    toast.success(`Assignment created. ${result.generatedEdges ?? 0} edge(s) generated. Activate it to start calculating.`);
                    assignments.refresh();
                }}
            />
        </PageContainer>
    );
};

const AssignmentRow: React.FC<{
    assignment: Store.ProcedureAssignmentListItemModel;
    canAssign: boolean;
    onAction: (label: string, run: () => Promise<Store.AssignmentGenerationModel>) => Promise<void>;
    onResolved: (resolution: Store.ExactSelectorResolutionModel) => void;
}> = ({ assignment, canAssign, onAction, onResolved }) => {
    const id = assignment.id ?? "";

    const activate = useAction(() => onAction("Activated", () => Store.activateAssignment(id)));
    const deactivate = useAction(() => onAction("Deactivated", () => Store.deactivateAssignment(id)));
    const regenerate = useAction(() => onAction("Regenerated", () => Store.regenerateAssignment(id)));
    const check = useAction(async () => {
        const resolution = await Store.resolveExactSelectors(assignment.procedureVersionID ?? "", assignment.qualificationModuleID ?? "");
        onResolved(resolution);
    });

    return (
        <Tr>
            <Td>
                <span className="font-medium text-text-strong">{assignment.procedureName}</span>
            </Td>
            <Td>
                <Badge tone="success">v{assignment.versionNumber}</Badge>
            </Td>
            <Td>{assignment.qualificationModuleName}</Td>
            <Td align="right">{assignment.generatedEdges ?? 0}</Td>
            <Td>
                <Badge tone={assignment.isActive ? "success" : "neutral"}>{assignment.isActive ? "Active" : "Inactive"}</Badge>
            </Td>
            <TdActions>
                <IconButton icon={["fad", "magnifying-glass"]} label="Check against module" onClick={check.run} loading={check.busy} />
                {canAssign && (
                    <>
                        <IconButton
                            icon={["fad", "rotate"]}
                            label="Regenerate edges"
                            onClick={regenerate.run}
                            loading={regenerate.busy}
                        />
                        {assignment.isActive ? (
                            <IconButton icon={["fad", "pause"]} label="Deactivate" onClick={deactivate.run} loading={deactivate.busy} />
                        ) : (
                            <IconButton
                                icon={["fad", "play"]}
                                variant="outline-success"
                                label="Activate"
                                onClick={activate.run}
                                loading={activate.busy}
                            />
                        )}
                    </>
                )}
            </TdActions>
        </Tr>
    );
};

const CreateAssignmentModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onCreated: (result: Store.AssignmentGenerationModel) => void;
}> = ({ isOpen, onClose, onCreated }) => {
    const [moduleID, setModuleID] = React.useState("");
    const [procedureID, setProcedureID] = React.useState("");
    const [versionID, setVersionID] = React.useState("");

    const procedures = useAsync(() => Store.listProcedures(), [], { initial: [] });
    const versions = useAsync(() => (procedureID ? Store.listVersions(procedureID) : Promise.resolve([])), [procedureID], {
        initial: [],
        enabled: !!procedureID,
    });

    // Only a published version can be assigned - a draft is still being edited.
    const publishedVersions = (versions.data ?? []).filter((v) => v.status === "Published");

    React.useEffect(() => {
        if (isOpen) {
            setModuleID("");
            setProcedureID("");
            setVersionID("");
        }
    }, [isOpen]);

    const create = useAction(async () => {
        const result = await Store.createAssignment(moduleID, versionID);
        onCreated(result);
    });

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="New assignment"
            icon={["fad", "link"]}
            footer={
                <>
                    <Button variant="outline-secondary" onClick={onClose} disabled={create.busy}>
                        Cancel
                    </Button>
                    <Button onClick={create.run} loading={create.busy} disabled={!moduleID || !versionID}>
                        Create
                    </Button>
                </>
            }
        >
            <ModalBody>
                <div className="mb-4">
                    <ModulePicker value={moduleID} onChange={setModuleID} />
                </div>

                <Field label="Procedure" required>
                    <Select
                        value={procedureID}
                        placeholder="Select a procedure"
                        onChange={(e) => {
                            setProcedureID(e.target.value);
                            setVersionID("");
                        }}
                    >
                        {(procedures.data ?? []).map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.name}
                            </option>
                        ))}
                    </Select>
                </Field>

                <Field label="Version" required hint="Only published versions can be assigned.">
                    <Select
                        value={versionID}
                        placeholder={procedureID ? "Select a version" : "Pick a procedure first"}
                        disabled={!procedureID}
                        onChange={(e) => setVersionID(e.target.value)}
                    >
                        {publishedVersions.map((v) => (
                            <option key={v.id} value={v.id}>
                                Version {v.versionNumber}
                            </option>
                        ))}
                    </Select>
                </Field>

                {procedureID && publishedVersions.length === 0 && (
                    <Banner tone="warning">That procedure has no published version yet. Publish one before assigning it.</Banner>
                )}

                {create.error && <Banner tone="danger">{create.error}</Banner>}
            </ModalBody>
        </Modal>
    );
};

export default AssignmentsIndex;
